import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Grid3X3, Loader2, Lock } from 'lucide-react';
import { format, isWeekend } from 'date-fns';
import {
  getJobsForAuditorDate, getJobCustomer, isFirstDayOfJob,
  getJobSpanForAuditor, getJobLabel, getPrintScaleClass,
  getActiveAuditorsWithJobs, getDateRange, getBankHoliday, leaveColors
} from '../../utils/reportHelpers';
import ReportPrintHeader from './ReportPrintHeader';
import { LockLegend } from '../JobLock';

export default function ScheduleMatrixReport({
  jobs, loading, auditors, startDate, endDate, bankHolidays, reportTitle, region
}) {
  const dates = getDateRange(startDate, endDate);
  const activeAuditors = getActiveAuditorsWithJobs(jobs, auditors);
  const printScaleClass = getPrintScaleClass(activeAuditors.length);

  return (
    <div className={`space-y-6 print-section ${printScaleClass}`} data-testid="schedule">
      <ReportPrintHeader title={reportTitle} startDate={startDate} endDate={endDate} region={region} />

      <Card>
        <CardHeader className="print:hidden">
          <CardTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5" />
            Schedule Matrix
          </CardTitle>
          <CardDescription>
            Visual grid showing auditor assignments across dates. Weekends shaded grey, bank holidays amber.
            {activeAuditors.length < auditors.length && (
              <span className="ml-2 text-amber-600">
                (Showing {activeAuditors.length} auditors with jobs in this period)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeAuditors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No auditors with jobs in the selected date range
            </div>
          ) : (
            <div className="overflow-x-auto print:overflow-visible">
              {/* Axis Label for Auditors - screen only */}
              <div className="flex items-center mb-2 print:hidden">
                <div className="w-[120px]"></div>
                <div className="flex-1 text-center">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider px-2 py-1 bg-slate-100 rounded">
                    &larr; Auditors &rarr;
                  </span>
                </div>
              </div>

              <div className="flex">
                {/* Axis Label for Date - screen only */}
                <div className="flex flex-col items-center justify-center pr-1 print:hidden">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    Dates &darr;
                  </span>
                </div>

                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="border p-2 bg-slate-100 text-left sticky left-0 z-10" style={{ width: '120px', minWidth: '120px' }}>
                        Date
                      </th>
                      {activeAuditors.map(auditor => (
                        <th
                          key={auditor.id}
                          className="border p-2 text-center bg-slate-50"
                          style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}
                        >
                          <div className="font-medium text-slate-800">{auditor.first_name}</div>
                          <div className="font-medium text-slate-800 text-xs">{auditor.last_name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                <tbody>
                  {dates.map(date => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const holiday = getBankHoliday(bankHolidays, dateStr);
                    const weekend = isWeekend(date);

                    return (
                      <tr key={dateStr}>
                        <td
                          className={`border p-2 sticky left-0 z-10 font-medium ${
                            holiday ? 'bg-amber-100' : weekend ? 'bg-slate-200' : 'bg-white'
                          }`}
                          style={{ width: '120px', minWidth: '120px' }}
                        >
                          <div className="flex flex-col">
                            <span>{format(date, 'EEE d MMM')}</span>
                            {holiday && (
                              <span className="text-[9px] text-amber-700 font-normal">{holiday.name}</span>
                            )}
                          </div>
                        </td>
                        {activeAuditors.map(auditor => {
                          const dayJobs = getJobsForAuditorDate(jobs, auditor.id, dateStr);

                          return (
                            <td
                              key={auditor.id}
                              className={`border p-1 align-top ${
                                holiday ? 'bg-amber-50' : weekend ? 'bg-slate-100' : ''
                              }`}
                              style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}
                            >
                              {dayJobs.map(job => {
                                const customer = getJobCustomer(job);
                                const isFirst = isFirstDayOfJob(job, dateStr, auditor.id);
                                const span = getJobSpanForAuditor(job, auditor.id);
                                const isLeave = job.event_type?.startsWith('LEAVE');
                                const isLocked = !!job.is_locked;

                                const cellStyle = isLeave
                                  ? {
                                      backgroundColor: leaveColors[job.event_type]?.bg || '#e5e7eb',
                                      borderLeft: `2px solid ${leaveColors[job.event_type]?.border || '#9ca3af'}`
                                    }
                                  : {
                                      backgroundColor: customer?.colour ? `${customer.colour}25` : '#e5e7eb',
                                      borderLeft: `2px solid ${customer?.colour || '#9ca3af'}`
                                    };

                                // iter341 — locked cells get a 3px amber outline.
                                if (isLocked) {
                                  cellStyle.outline = '3px solid #f59e0b';
                                  cellStyle.outlineOffset = '-3px';
                                }

                                return (
                                  <div
                                    key={job.id}
                                    className={`job-cell rounded px-1 py-0.5 mb-0.5 text-[10px] ${
                                      isFirst ? 'font-medium' : 'opacity-60'
                                    }`}
                                    style={cellStyle}
                                    title={isLeave
                                      ? `${getJobLabel(job)} (${span > 1 ? `${span} days` : 'Single day'})${isLocked ? ' — Locked' : ''}`
                                      : `${job.site?.name} - ${job.service?.name} (${span > 1 ? `Multi-day job` : 'Single day'})${isLocked ? ' — Locked' : ''}`
                                    }
                                    data-testid={isLocked ? `schedule-matrix-locked-cell-${job.id}` : undefined}
                                  >
                                    <div className="break-words leading-tight flex items-center gap-1">
                                      {isLocked && (
                                        <Lock className="h-2.5 w-2.5 text-amber-700 flex-shrink-0" aria-label="Locked" />
                                      )}
                                      <span className="break-words">{getJobLabel(job)}</span>
                                    </div>
                                    {isFirst && span > 1 && (
                                      <div className="text-[8px] opacity-70">({span} days)</div>
                                    )}
                                  </div>
                                );
                              })}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {dates.length === 0 && (
                    <tr>
                      <td colSpan={activeAuditors.length + 1} className="text-center py-8 text-muted-foreground">
                        No dates in selected range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend - hide on print, info is in header */}
      <div className="flex flex-wrap gap-4 text-xs print:hidden">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-200 border rounded"></div>
          <span>Weekend</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-amber-100 border rounded"></div>
          <span>Bank Holiday</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border rounded" style={{ borderLeft: '3px solid #3B82F6', backgroundColor: '#3B82F620' }}></div>
          <span>Job (colored by customer)</span>
        </div>
      </div>

      {/* iter341 — lock legend */}
      <div className="print:hidden">
        <LockLegend />
      </div>
    </div>
  );
}
